import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';

/**
 * Learning Page - Training docs with quiz system
 * Uses shared_documents table with category='training'
 */
export default function LearningPage() {
  const { currentUser, showToast } = useContext(AppContext);
  const [docs, setDocs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showQuizBuilder, setShowQuizBuilder] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [callMeetingRequested, setCallMeetingRequested] = useState(false);
  const [requestingMeeting, setRequestingMeeting] = useState(false);

  useEffect(() => {
    setIsAdmin(currentUser?.role === 'admin');
    loadLearningData();
  }, []);

  const loadLearningData = async () => {
    setLoading(true);
    try {
      // Load training documents from shared_documents with category='training'
      const { data: docsData, error: docsError } = await supabase
        .from('shared_documents')
        .select('*')
        .contains('allowed_roles', [currentUser.role])
        .order('order_number', { ascending: true });
      
      if (docsError) throw docsError;
      
      // Filter to only training category
      const trainingDocs = (docsData || []).filter(d => d.category === 'training');
      setDocs(trainingDocs);

      // Load quiz questions - filtered by user role
      const { data: questionsData } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('is_active', true)
        .contains('allowed_roles', [currentUser.role]) // Only questions for this role
        .order('order_number', { ascending: true });
      setQuestions(questionsData || []);

      // Load user progress
      const { data: progressData } = await supabase
        .from('user_learning_progress')
        .select('*')
        .eq('user_id', currentUser.id);
      
      const progressMap = {};
      (progressData || []).forEach(p => { progressMap[p.doc_id] = p.is_completed; });
      setProgress(progressMap);

      // Check user quiz status
      const { data: userData } = await supabase
        .from('users')
        .select('is_ready_for_call, quiz_submitted_at, quiz_score, quiz_passed, created_at, call_meeting_requested')
        .eq('id', currentUser.id)
        .single();
      
      setIsReady(userData?.is_ready_for_call || false);
      setQuizScore(userData?.quiz_score);
      setCallMeetingRequested(userData?.call_meeting_requested || false);
      
      // Calculate days remaining (7 days from account creation)
      if (userData?.created_at && !userData?.is_ready_for_call) {
        const createdDate = new Date(userData.created_at);
        const now = new Date();
        const deadlineDate = new Date(createdDate.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days
        const diffTime = deadlineDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysRemaining(Math.max(0, diffDays));
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const markDocComplete = async (docId) => {
    try {
      await supabase
        .from('user_learning_progress')
        .upsert({
          user_id: currentUser.id,
          doc_id: docId,
          is_completed: true,
          completed_at: new Date().toISOString(),
        });
      
      setProgress(prev => ({ ...prev, [docId]: true }));
      showToast('✅ تم تعليم المستند كمكتمل');
      
      const newProgress = { ...progress, [docId]: true };
      const completedCount = Object.values(newProgress).filter(Boolean).length;
      const percentage = docs.length > 0 ? Math.round((completedCount / docs.length) * 100) : 0;
      
      await supabase
        .from('users')
        .update({ learning_progress: percentage })
        .eq('id', currentUser.id);
    } catch (err) {
      showToast('❌ فشل في تحديث التقدم', 'error');
    }
  };

  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const requestCallMeeting = async () => {
    if (!allDocsCompleted) {
      showToast('⚠️ يجب إكمال جميع المستندات أولاً', 'error');
      return;
    }

    if (!quizScore || quizScore < 70) {
      showToast('⚠️ يجب اجتياز الاختبار بنسبة 70% على الأقل', 'error');
      return;
    }

    setRequestingMeeting(true);
    try {
      await supabase
        .from('users')
        .update({
          call_meeting_requested: true,
          call_meeting_requested_at: new Date().toISOString(),
        })
        .eq('id', currentUser.id);

      setCallMeetingRequested(true);
      showToast('✅ تم إرسال طلب اجتماع المكالمة بنجاح!');
    } catch (err) {
      console.error('Request meeting error:', err);
      showToast('❌ فشل في إرسال الطلب', 'error');
    } finally {
      setRequestingMeeting(false);
    }
  };

  const submitQuiz = async () => {
    if (Object.keys(quizAnswers).length < questions.length) {
      showToast(`⚠️ يجب الإجابة على جميع الأسئلة`, 'error');
      return;
    }

    setSubmitting(true);
    try {
      let correctCount = 0;
      const finalAnswers = {};
      
      questions.forEach((q, index) => {
        finalAnswers[index + 1] = quizAnswers[q.id];
        if (quizAnswers[q.id] === q.correct_option) correctCount++;
      });

      const scorePercentage = Math.round((correctCount / questions.length) * 100);
      const passed = scorePercentage >= 70;

      await supabase
        .from('user_quiz_attempts')
        .insert({
          user_id: currentUser.id,
          total_questions: questions.length,
          correct_answers: correctCount,
          score_percentage: scorePercentage,
          passed,
          answers: finalAnswers,
          review_status: 'pending',
        });

      await supabase
        .from('users')
        .update({
          quiz_submitted_at: new Date().toISOString(),
          quiz_score: scorePercentage,
          quiz_passed: passed,
          is_ready_for_call: passed,
          ready_at: passed ? new Date().toISOString() : null,
        })
        .eq('id', currentUser.id);

      setQuizScore(scorePercentage);
      setIsReady(passed);
      setShowQuiz(false);
      showToast(passed 
        ? `🎉 مبروك! نجحت بنسبة ${scorePercentage}%` 
        : `⚠️ نتيجتك ${scorePercentage}% - تحتاج 70%`
      );
    } catch (err) {
      showToast('❌ فشل في إرسال الاختبار', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const completedCount = Object.values(progress).filter(Boolean).length;
  const progressPercentage = docs.length > 0 ? Math.round((completedCount / docs.length) * 100) : 0;
  const allDocsCompleted = completedCount === docs.length;

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
              📚 مسار التدريب ({docs.length})
            </h1>
            <p style={{ color: '#000000', fontSize: 15 }}>
              مواد التدريب خطوة بخطوة
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {isAdmin && (
              <button
                onClick={() => window.location.href = '/admin/documents'}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                ➕ إدارة مواد التدريب
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowQuizBuilder(!showQuizBuilder)}
                style={{
                  padding: '10px 20px',
                  background: showQuizBuilder ? '#f59e0b' : '#4ecdc4',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                {showQuizBuilder ? '📖 التدريب' : '✏️ الأسئلة'}
              </button>
            )}
          </div>
        </div>
      </div>

      {isAdmin && showQuizBuilder ? (
        <AdminQuizBuilder questions={questions} onLoad={loadLearningData} />
      ) : (
        <>
          {/* Progress */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: '#000000', fontWeight: 700 }}>تقدمك في التدريب</span>
              <span style={{ color: '#60a5fa', fontSize: 18, fontWeight: 900 }}>{progressPercentage}%</span>
            </div>
            <div style={{ height: 12, background: '#ffffff', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{
                width: `${progressPercentage}%`,
                height: '100%',
                background: progressPercentage === 100 ? 'linear-gradient(90deg, #10b981, #059669)' : 'linear-gradient(90deg, #4ecdc4, #38b2ac)',
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          {/* 7-Day Deadline Warning */}
          {!isAdmin && daysRemaining !== null && !isReady && (
            <div style={{ 
              background: daysRemaining <= 2 
                ? 'linear-gradient(135deg, #ef444422, #ef444411)' 
                : daysRemaining <= 4 
                  ? 'linear-gradient(135deg, #f59e0b22, #f59e0b11)' 
                  : 'linear-gradient(135deg, #4ecdc422, #4ecdc411)',
              border: `2px solid ${daysRemaining <= 2 ? '#ef444466' : daysRemaining <= 4 ? '#f59e0b66' : '#4ecdc466'}`,
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
              textAlign: 'center',
            }}>
              <span style={{ fontSize: 40, display: 'block', marginBottom: 8 }}>
                {daysRemaining <= 2 ? '🚨' : daysRemaining <= 4 ? '⚠️' : '⏰'}
              </span>
              <h3 style={{ 
                color: daysRemaining <= 2 ? '#ef4444' : daysRemaining <= 4 ? '#f59e0b' : '#4ecdc4', 
                fontSize: 18, 
                fontWeight: 900, 
                marginBottom: 8 
              }}>
                {daysRemaining === 0 
                  ? 'انتهت المهلة!' 
                  : daysRemaining === 1 
                    ? 'يبقى يوم واحد!' 
                    : `يبقى ${daysRemaining} أيام`}
              </h3>
              <p style={{ color: '#000000', fontSize: 14, marginBottom: 0 }}>
                لديك 7 أيام لإكمال التدريب للحفاظ على حسابك مفعلاً
              </p>
            </div>
          )}

          {/* Request Call Meeting Button */}
          {!isAdmin && allDocsCompleted && quizScore >= 70 && !callMeetingRequested && (
            <div style={{ 
              background: 'linear-gradient(135deg, #10b98122, #10b98111)',
              border: '2px solid #10b98144',
              borderRadius: 12,
              padding: 24,
              marginBottom: 24,
              textAlign: 'center',
            }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>🎉</span>
              <h3 style={{ color: '#10b981', fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
                مبروك! أكملت التدريب بنجاح
              </h3>
              <p style={{ color: '#000000', fontSize: 14, marginBottom: 20 }}>
                نتيجتك: {quizScore}% - لقد اجتزت الاختبار بنجاح!
              </p>
              <button
                onClick={requestCallMeeting}
                disabled={requestingMeeting}
                style={{
                  padding: '14px 32px',
                  background: requestingMeeting ? '#64748b' : 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: requestingMeeting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  if (!requestingMeeting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                {requestingMeeting ? (
                  <>
                    <span style={{ fontSize: 18 }}>⏳</span>
                    جاري الإرسال...
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 18 }}>📞</span>
                    أكملت تدريبي وأطلب اجتماع مكالمة
                  </>
                )}
              </button>
            </div>
          )}

          {/* Meeting Requested Confirmation */}
          {!isAdmin && callMeetingRequested && (
            <div style={{ 
              background: 'linear-gradient(135deg, #10b98122, #10b98111)',
              border: '2px solid #10b98144',
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
              textAlign: 'center',
            }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 8 }}>✅</span>
              <h3 style={{ color: '#10b981', fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
                تم إرسال طلب اجتماع المكالمة!
              </h3>
              <p style={{ color: '#000000', fontSize: 14, marginBottom: 0 }}>
                سيتم التواصل معك قريباً لتحديد موعد الاجتماع
              </p>
            </div>
          )}

          {/* Quiz Prompt */}
          {allDocsCompleted && !showQuiz && quizScore === null && (
            <div style={{ background: 'linear-gradient(135deg, #f59e0b22, #f59e0b11)', border: '2px solid #f59e0b44', borderRadius: 12, padding: 24, marginBottom: 24, textAlign: 'center' }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>📝</span>
              <h2 style={{ color: '#fcd34d', fontSize: 22, fontWeight: 900, marginBottom: 8 }}>
                أكملت جميع مواد التدريب!
              </h2>
              <p style={{ color: '#000000', fontSize: 15, marginBottom: 20 }}>
                {questions.length} أسئلة - تحتاج {Math.ceil(questions.length * 0.7)} إجابات صحيحة (70%)
              </p>
              <button onClick={() => setShowQuiz(true)} style={{
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none',
                borderRadius: 10,
                color: '#fff',
                fontSize: 18,
                fontWeight: 900,
                cursor: 'pointer',
                fontFamily: "'Cairo', sans-serif",
              }}>
                📝 ابدأ الاختبار
              </button>
            </div>
          )}

          {/* Quiz UI */}
          {showQuiz && (
            <QuizUI
              questions={questions}
              answers={quizAnswers}
              onAnswer={handleQuizAnswer}
              onSubmit={submitQuiz}
              submitting={submitting}
            />
          )}

          {/* Results */}
          {quizScore !== null && (
            <div style={{
              background: quizScore >= 70 ? 'linear-gradient(135deg, #10b98122, #10b98111)' : 'linear-gradient(135deg, #ef444422, #ef444411)',
              border: `2px solid ${quizScore >= 70 ? '#10b98144' : '#ef444444'}`,
              borderRadius: 12,
              padding: 24,
              marginBottom: 24,
              textAlign: 'center',
            }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{quizScore >= 70 ? '🎉' : '⚠️'}</span>
              <h2 style={{ color: quizScore >= 70 ? '#10b981' : '#ef4444', fontSize: 22, fontWeight: 900, marginBottom: 8 }}>
                {quizScore >= 70 ? 'مبروك! نجحت' : 'لم تجتز الاختبار'}
              </h2>
              <p style={{ color: '#000000', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{quizScore}%</p>
              {quizScore >= 70 ? (
                <p style={{ color: '#10b981' }}>✅ تم إرسال نتيجتك للمدير</p>
              ) : (
                <button onClick={() => { setShowQuiz(true); setQuizAnswers({}); setQuizScore(null); }} style={{
                  padding: '12px 24px',
                  background: '#4ecdc4',
                  border: 'none',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}>
                  🔄 إعادة الاختبار
                </button>
              )}
            </div>
          )}

          {/* Docs */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>جاري التحميل...</div>
          ) : (
            <TrainingDocsList docs={docs} progress={progress} onComplete={markDocComplete} />
          )}
        </>
      )}
    </div>
  );
}

// Training Docs List Component
function TrainingDocsList({ docs, progress, onComplete }) {
  const isYouTubeUrl = (url) => url?.includes('youtube.com') || url?.includes('youtu.be');
  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {docs.map((doc, index) => {
        const isCompleted = progress[doc.id];
        const youtubeId = isYouTubeUrl(doc.file_url) ? getYouTubeId(doc.file_url) : null;

        return (
          <div key={doc.id} style={{
            background: '#ffffff',
            border: isCompleted ? '2px solid #10b98144' : '1px solid #e2e8f0',
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            <div style={{ padding: '16px 20px', background: isCompleted ? '#10b98111' : 'transparent', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: isCompleted ? '#10b98122' : '#4ecdc422',
                  border: `2px solid ${isCompleted ? '#10b981' : '#4ecdc4'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 900, color: isCompleted ? '#10b981' : '#4ecdc4',
                }}>
                  {isCompleted ? '✓' : index + 1}
                </div>
                <div>
                  <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, margin: 0 }}>{doc.title}</h3>
                  {doc.description && <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>{doc.description}</p>}
                </div>
              </div>
              <button onClick={() => onComplete(doc.id)} disabled={isCompleted} style={{
                padding: '8px 16px',
                background: isCompleted ? '#10b98122' : '#4ecdc4',
                border: 'none',
                borderRadius: 6,
                color: isCompleted ? '#10b981' : '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: isCompleted ? 'default' : 'pointer',
                fontFamily: "'Cairo', sans-serif",
              }}>
                {isCompleted ? '✅ مكتمل' : '📖 قرأت'}
              </button>
            </div>

            {youtubeId && (
              <div style={{ padding: 20 }}>
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 8 }}>
                  <iframe src={`https://www.youtube.com/embed/${youtubeId}`} title={doc.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allowFullScreen />
                </div>
              </div>
            )}

            {!youtubeId && (
              <div style={{ padding: 20 }}>
                <a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 20px',
                    background: '#4ecdc422',
                    border: '1px solid #4ecdc444',
                    borderRadius: 8,
                    color: '#60a5fa',
                    fontSize: 14,
                    fontWeight: 700,
                    textDecoration: 'none',
                    fontFamily: "'Cairo', sans-serif",
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#4ecdc444';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#4ecdc422';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {doc.file_type?.includes('pdf') || doc.file_type?.includes('document') ? '📄' : '🔗'} فتح المستند
                </a>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Quiz UI Component
function QuizUI({ questions, answers, onAnswer, onSubmit, submitting }) {
  const answeredCount = Object.keys(answers).length;

  return (
    <div style={{
      background: '#ffffff',
      border: '2px solid #f59e0b44',
      borderRadius: 12,
      padding: 24,
      marginBottom: 24,
    }}>
      <div style={{ marginBottom: 24, textAlign: 'center' }}>
        <h2 style={{ color: '#fcd34d', fontSize: 24, fontWeight: 900, marginBottom: 8 }}>📝 الاختبار</h2>
        <p style={{ color: '#000000' }}>أجب على جميع الأسئلة ({answeredCount}/{questions.length})</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {questions.map((q, index) => (
          <div key={q.id} style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: 20,
          }}>
            <h3 style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              {index + 1}. {q.question_text}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { key: 'A', text: q.option_a },
                { key: 'B', text: q.option_b },
                { key: 'C', text: q.option_c },
                { key: 'D', text: q.option_d },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => onAnswer(q.id, opt.key)}
                  style={{
                    padding: '12px 16px',
                    background: answers[q.id] === opt.key ? '#4ecdc422' : 'transparent',
                    border: `2px solid ${answers[q.id] === opt.key ? '#4ecdc4' : '#e2e8f0'}`,
                    borderRadius: 8,
                    color: answers[q.id] === opt.key ? '#60a5fa' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: answers[q.id] === opt.key ? 700 : 500,
                    cursor: 'pointer',
                    textAlign: 'right',
                    fontFamily: "'Cairo', sans-serif",
                    transition: 'all 0.2s',
                  }}
                >
                  <strong>{opt.key}.</strong> {opt.text}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onSubmit}
        disabled={submitting || answeredCount < questions.length}
        style={{
          width: '100%',
          marginTop: 24,
          padding: '14px',
          background: answeredCount === questions.length ? 'linear-gradient(135deg, #10b981, #059669)' : '#64748b',
          border: 'none',
          borderRadius: 10,
          color: '#fff',
          fontSize: 18,
          fontWeight: 900,
          cursor: answeredCount === questions.length ? 'pointer' : 'not-allowed',
          fontFamily: "'Cairo', sans-serif",
        }}
      >
        {submitting ? 'جاري الإرسال...' : `✅ إرسال الاختبار (${answeredCount}/${questions.length})`}
      </button>
    </div>
  );
}

// Admin Quiz Builder Component
function AdminQuizBuilder({ questions, onLoad }) {
  const { currentUser, showToast } = useContext(AppContext);
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_option: 'A',
    order_number: questions.length + 1,
    allowed_roles: ['marketer', 'rep'], // Default: both marketer and rep
  });
  const [submitting, setSubmitting] = useState(false);

  const ROLES = [
    { value: 'marketer', label: 'منسقة صفقات', icon: '📣' },
    { value: 'rep', label: 'مندوب ميداني', icon: '🤝' },
    { value: 'sales_manager', label: 'مدير مبيعات', icon: '🎯' },
    { value: 'data_entry', label: 'مدخل بيانات', icon: '⌨️' },
    { value: 'accountant', label: 'محاسب', icon: '💼' },
  ];

  const toggleRole = (role) => {
    setNewQuestion(prev => {
      const roles = prev.allowed_roles || [];
      if (roles.includes(role)) {
        return { ...prev, allowed_roles: roles.filter(r => r !== role) };
      } else {
        return { ...prev, allowed_roles: [...roles, role] };
      }
    });
  };

  const addQuestion = async () => {
    if (!newQuestion.question_text || !newQuestion.option_a || !newQuestion.option_b || !newQuestion.option_c || !newQuestion.option_d) {
      showToast('⚠️ يجب ملء جميع الحقول', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('quiz_questions')
        .insert([newQuestion]);
      
      if (error) throw error;
      
      showToast('✅ تم إضافة السؤال');
      setNewQuestion({
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_option: 'A',
        order_number: questions.length + 1,
      });
      onLoad();
    } catch (err) {
      showToast('❌ فشل في إضافة السؤال', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteQuestion = async (id) => {
    if (!confirm('هل أنت متأكد من حذف هذا السؤال؟')) return;

    try {
      const { error } = await supabase.from('quiz_questions').delete().eq('id', id);
      if (error) throw error;
      showToast('✅ تم حذف السؤال');
      onLoad();
    } catch (err) {
      showToast('❌ فشل في الحذف', 'error');
    }
  };

  return (
    <div>
      {/* Add Question Form */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>➕ إضافة سؤال جديد</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ color: '#000000', fontSize: 13, marginBottom: 4, display: 'block' }}>نص السؤال</label>
            <textarea
              value={newQuestion.question_text}
              onChange={e => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
              placeholder="اكتب السؤال هنا..."
              rows={3}
              style={{
                width: '100%',
                padding: 12,
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                color: '#000000',
                fontSize: 14,
                fontFamily: "'Cairo', sans-serif",
              }}
            />
          </div>

          {['option_a', 'option_b', 'option_c', 'option_d'].map((opt, i) => (
            <div key={opt}>
              <label style={{ color: '#000000', fontSize: 13, marginBottom: 4, display: 'block' }}>
                الخيار {String.fromCharCode(65 + i)} {newQuestion.correct_option === String.fromCharCode(65 + i) && '✅'}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={newQuestion[opt]}
                  onChange={e => setNewQuestion({ ...newQuestion, [opt]: e.target.value })}
                  placeholder={`الخيار ${String.fromCharCode(65 + i)}`}
                  style={{
                    flex: 1,
                    padding: 10,
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    color: '#000000',
                    fontSize: 14,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                />
                <button
                  onClick={() => setNewQuestion({ ...newQuestion, correct_option: String.fromCharCode(65 + i) })}
                  style={{
                    padding: '10px 16px',
                    background: newQuestion.correct_option === String.fromCharCode(65 + i) ? '#10b981' : '#e2e8f0',
                    border: 'none',
                    borderRadius: 8,
                    color: newQuestion.correct_option === String.fromCharCode(65 + i) ? '#fff' : '#94a3b8',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {newQuestion.correct_option === String.fromCharCode(65 + i) ? '✅ صحيح' : 'تحديد'}
                </button>
              </div>
            </div>
          ))}

          {/* Role Selector */}
          <div>
            <label style={{ color: '#000000', fontSize: 13, marginBottom: 8, display: 'block' }}>
              مخصص لـ * (يمكن اختيار أكثر من دور)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ROLES.map(role => {
                const isSelected = (newQuestion.allowed_roles || []).includes(role.value);
                return (
                  <button
                    key={role.value}
                    onClick={() => toggleRole(role.value)}
                    style={{
                      padding: '8px 16px',
                      background: isSelected ? '#4ecdc422' : '#ffffff',
                      border: `2px solid ${isSelected ? '#4ecdc4' : '#e2e8f0'}`,
                      borderRadius: 8,
                      color: isSelected ? '#60a5fa' : '#94a3b8',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: "'Cairo', sans-serif",
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{role.icon}</span>
                    <span>{role.label}</span>
                    {isSelected && <span style={{ marginLeft: 4 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            onClick={addQuestion}
            disabled={submitting}
            style={{
              padding: '12px',
              background: submitting ? '#64748b' : 'linear-gradient(135deg, #4ecdc4, #38b2ac)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {submitting ? 'جاري الإضافة...' : '➕ إضافة السؤال'}
          </button>
        </div>
      </div>

      {/* Existing Questions */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24 }}>
        <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
          📋 الأسئلة الحالية ({questions.length})
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {questions.map((q, index) => (
            <div key={q.id} style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                  {index + 1}. {q.question_text}
                </div>
                <div style={{ color: '#64748b', fontSize: 12 }}>
                  ✅ الإجابة الصحيحة: {q[`option_${q.correct_option.toLowerCase()}`]}
                </div>
              </div>
              <button
                onClick={() => deleteQuestion(q.id)}
                style={{
                  padding: '6px 12px',
                  background: '#ef444422',
                  border: '1px solid #ef444444',
                  borderRadius: 6,
                  color: '#ef4444',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                🗑️ حذف
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
